import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { CheckCircle } from 'lucide-react';
export function ErrorsGuidePage() {
  const errorHandlingCode = {
    curl: `# cURL returns the full error response
curl https://api.obseq.com/v1/payments/payouts \\
  -H "Authorization: Bearer sk_test_..." \\
  -d '{"amount": "invalid"}'

# Response (400):
# {
#   "error": {
#     "type": "invalid_request_error",
#     "code": "invalid_parameter",
#     "message": "The parameter 'amount' must be an integer.",
#     "param": "amount"
#   }
# }`,
    node: `try {
  const response = await fetch('https://api.obseq.com/v1/payments/payouts', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${apiKey}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ amount: 'invalid' })
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('API Error:', error.error.type, error.error.message);
    throw new Error(error.error.message);
  }

  const data = await response.json();
} catch (err) {
  console.error('Request failed:', err);
}`,
    python: `import requests

try:
    r = requests.post(
        'https://api.obseq.com/v1/payments/payouts',
        headers={'Authorization': f'Bearer {api_key}'},
        json={'amount': 'invalid'}
    )
    r.raise_for_status()
    data = r.json()
except requests.exceptions.HTTPError as e:
    error = e.response.json()
    print(f"API Error: {error['error']['type']}")
    print(f"Message: {error['error']['message']}")
except requests.exceptions.RequestException as e:
    print(f"Request failed: {e}")`,
    go: `resp, err := client.Do(req)
if err != nil {
    log.Fatal("Request failed:", err)
}
defer resp.Body.Close()

if resp.StatusCode >= 400 {
    var errorResp struct {
        Error struct {
            Type    string \`json:"type"\`
            Code    string \`json:"code"\`
            Message string \`json:"message"\`
        } \`json:"error"\`
    }
    json.NewDecoder(resp.Body).Decode(&errorResp)
    log.Printf("API Error: %s - %s", errorResp.Error.Type, errorResp.Error.Message)
    return
}`,
    java: `try {
    Response response = client.newCall(request).execute();
    
    if (!response.isSuccessful()) {
        JSONObject error = new JSONObject(response.body().string())
            .getJSONObject("error");
        System.err.println("API Error: " + error.getString("type"));
        System.err.println("Message: " + error.getString("message"));
        throw new IOException("API request failed");
    }
    
    JSONObject data = new JSONObject(response.body().string());
} catch (IOException e) {
    System.err.println("Request failed: " + e.getMessage());
}`,
    php: `try {
    $response = $client->post('https://api.obseq.com/v1/payments/payouts', [
        'headers' => ['Authorization' => 'Bearer ' . $apiKey],
        'json' => ['amount' => 'invalid']
    ]);
    $data = json_decode($response->getBody(), true);
} catch (\\GuzzleHttp\\Exception\\ClientException $e) {
    $error = json_decode($e->getResponse()->getBody(), true);
    error_log("API Error: " . $error['error']['type']);
    error_log("Message: " . $error['error']['message']);
} catch (\\Exception $e) {
    error_log("Request failed: " . $e->getMessage());
}`,
    ruby: `begin
  response = HTTParty.post(
    'https://api.obseq.com/v1/payments/payouts',
    headers: { 'Authorization' => "Bearer #{api_key}" },
    body: { amount: 'invalid' }.to_json
  )
  
  if response.code >= 400
    error = response.parsed_response['error']
    puts "API Error: #{error['type']}"
    puts "Message: #{error['message']}"
    raise StandardError, error['message']
  end
  
  data = response.parsed_response
rescue => e
  puts "Request failed: #{e.message}"
end`,
    dotnet: `try
{
    var response = await client.PostAsync(
        "https://api.obseq.com/v1/payments/payouts",
        new StringContent(json, Encoding.UTF8, "application/json")
    );

    if (!response.IsSuccessStatusCode)
    {
        var errorJson = await response.Content.ReadAsStringAsync();
        var error = JsonSerializer.Deserialize<ErrorResponse>(errorJson);
        Console.WriteLine($"API Error: {error.Error.Type}");
        Console.WriteLine($"Message: {error.Error.Message}");
        throw new HttpRequestException(error.Error.Message);
    }

    var data = await response.Content.ReadAsStringAsync();
}
catch (HttpRequestException ex)
{
    Console.WriteLine($"Request failed: {ex.Message}");
}`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'Guides',
    href: '/docs/guides'
  }, {
    label: 'Errors',
    href: '/docs/guides/errors'
  }]} onThisPage={[{
    title: 'Error Object',
    id: 'error-object'
  }, {
    title: 'Error Types',
    id: 'error-types'
  }, {
    title: 'HTTP Status Codes',
    id: 'http-status'
  }, {
    title: 'Common Error Codes',
    id: 'common-codes'
  }, {
    title: 'Handling Errors',
    id: 'handling'
  }, {
    title: 'Troubleshooting',
    id: 'troubleshooting'
  }]} data-id="element-1247">
      <h1 data-id="element-1248">Error Handling</h1>
      <p className="lead" data-id="element-1249">
        Obseq uses conventional HTTP status codes and returns consistent error
        objects to help you diagnose and handle failures.
      </p>

      <h2 id="error-object" data-id="element-1250">Error Object</h2>
      <p data-id="element-1251">All errors return a JSON object with this structure:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-1252">
        <code data-id="element-1253">{`{
  "error": {
    "type": "invalid_request_error",
    "code": "parameter_missing",
    "message": "The parameter 'amount' is required.",
    "param": "amount",
    "doc_url": "https://docs.obseq.com/docs/guides/errors#parameter_missing",
    "request_id": "req_01J9Z8V6X0C2H9J3Y7JQ3D2N5P",
    "details": [
      {
        "field": "amount",
        "reason": "required",
        "value": null
      }
    ]
  }
}`}</code>
      </pre>

      <h3 data-id="element-1254">Fields</h3>
      <table className="w-full text-sm my-4" data-id="element-1255">
        <thead data-id="element-1256">
          <tr className="border-b border-slate-200" data-id="element-1257">
            <th className="text-left py-2 pr-4" data-id="element-1258">Field</th>
            <th className="text-left py-2" data-id="element-1259">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-1260">
          <tr className="border-b border-slate-100" data-id="element-1261">
            <td className="py-2 pr-4" data-id="element-1262">
              <code data-id="element-1263">type</code>
            </td>
            <td className="py-2" data-id="element-1264">High-level error category</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1265">
            <td className="py-2 pr-4" data-id="element-1266">
              <code data-id="element-1267">code</code>
            </td>
            <td className="py-2" data-id="element-1268">
              Specific error code for programmatic handling
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1269">
            <td className="py-2 pr-4" data-id="element-1270">
              <code data-id="element-1271">message</code>
            </td>
            <td className="py-2" data-id="element-1272">Human-readable error description</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1273">
            <td className="py-2 pr-4" data-id="element-1274">
              <code data-id="element-1275">param</code>
            </td>
            <td className="py-2" data-id="element-1276">
              Parameter that caused the error (if applicable)
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1277">
            <td className="py-2 pr-4" data-id="element-1278">
              <code data-id="element-1279">doc_url</code>
            </td>
            <td className="py-2" data-id="element-1280">Link to relevant documentation</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1281">
            <td className="py-2 pr-4" data-id="element-1282">
              <code data-id="element-1283">request_id</code>
            </td>
            <td className="py-2" data-id="element-1284">Unique request identifier for support</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1285">
            <td className="py-2 pr-4" data-id="element-1286">
              <code data-id="element-1287">details</code>
            </td>
            <td className="py-2" data-id="element-1288">Additional context (optional)</td>
          </tr>
        </tbody>
      </table>

      <h2 id="error-types" data-id="element-1289">Error Types</h2>
      <p data-id="element-1290">Obseq uses these error types to categorize failures:</p>
      <table className="w-full text-sm my-4" data-id="element-1291">
        <thead data-id="element-1292">
          <tr className="border-b border-slate-200" data-id="element-1293">
            <th className="text-left py-2 pr-4" data-id="element-1294">Type</th>
            <th className="text-left py-2" data-id="element-1295">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-1296">
          <tr className="border-b border-slate-100" data-id="element-1297">
            <td className="py-2 pr-4" data-id="element-1298">
              <code data-id="element-1299">invalid_request_error</code>
            </td>
            <td className="py-2" data-id="element-1300">
              Bad parameters, missing fields, or invalid state
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1301">
            <td className="py-2 pr-4" data-id="element-1302">
              <code data-id="element-1303">authentication_error</code>
            </td>
            <td className="py-2" data-id="element-1304">Missing or invalid API key</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1305">
            <td className="py-2 pr-4" data-id="element-1306">
              <code data-id="element-1307">authorization_error</code>
            </td>
            <td className="py-2" data-id="element-1308">
              Insufficient permissions for the requested resource
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1309">
            <td className="py-2 pr-4" data-id="element-1310">
              <code data-id="element-1311">not_found_error</code>
            </td>
            <td className="py-2" data-id="element-1312">Resource does not exist</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1313">
            <td className="py-2 pr-4" data-id="element-1314">
              <code data-id="element-1315">conflict_error</code>
            </td>
            <td className="py-2" data-id="element-1316">Idempotency conflict or duplicate request</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1317">
            <td className="py-2 pr-4" data-id="element-1318">
              <code data-id="element-1319">rate_limit_error</code>
            </td>
            <td className="py-2" data-id="element-1320">Too many requests in a short period</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1321">
            <td className="py-2 pr-4" data-id="element-1322">
              <code data-id="element-1323">compliance_error</code>
            </td>
            <td className="py-2" data-id="element-1324">Transaction blocked by compliance rules</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1325">
            <td className="py-2 pr-4" data-id="element-1326">
              <code data-id="element-1327">liquidity_error</code>
            </td>
            <td className="py-2" data-id="element-1328">Insufficient liquidity or limits exceeded</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1329">
            <td className="py-2 pr-4" data-id="element-1330">
              <code data-id="element-1331">api_error</code>
            </td>
            <td className="py-2" data-id="element-1332">Server error (safe to retry)</td>
          </tr>
        </tbody>
      </table>

      <h2 id="http-status" data-id="element-1333">HTTP Status Codes</h2>
      <p data-id="element-1334">Error types map to standard HTTP status codes:</p>
      <table className="w-full text-sm my-4" data-id="element-1335">
        <thead data-id="element-1336">
          <tr className="border-b border-slate-200" data-id="element-1337">
            <th className="text-left py-2 pr-4" data-id="element-1338">Status Code</th>
            <th className="text-left py-2 pr-4" data-id="element-1339">Error Type</th>
            <th className="text-left py-2" data-id="element-1340">Meaning</th>
          </tr>
        </thead>
        <tbody data-id="element-1341">
          <tr className="border-b border-slate-100" data-id="element-1342">
            <td className="py-2 pr-4" data-id="element-1343">
              <code data-id="element-1344">400</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1345">
              <code data-id="element-1346">invalid_request_error</code>
            </td>
            <td className="py-2" data-id="element-1347">Bad Request</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1348">
            <td className="py-2 pr-4" data-id="element-1349">
              <code data-id="element-1350">401</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1351">
              <code data-id="element-1352">authentication_error</code>
            </td>
            <td className="py-2" data-id="element-1353">Unauthorized</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1354">
            <td className="py-2 pr-4" data-id="element-1355">
              <code data-id="element-1356">403</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1357">
              <code data-id="element-1358">authorization_error</code>
            </td>
            <td className="py-2" data-id="element-1359">Forbidden</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1360">
            <td className="py-2 pr-4" data-id="element-1361">
              <code data-id="element-1362">403</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1363">
              <code data-id="element-1364">compliance_error</code>
            </td>
            <td className="py-2" data-id="element-1365">Forbidden (compliance block)</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1366">
            <td className="py-2 pr-4" data-id="element-1367">
              <code data-id="element-1368">404</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1369">
              <code data-id="element-1370">not_found_error</code>
            </td>
            <td className="py-2" data-id="element-1371">Not Found</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1372">
            <td className="py-2 pr-4" data-id="element-1373">
              <code data-id="element-1374">409</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1375">
              <code data-id="element-1376">conflict_error</code>
            </td>
            <td className="py-2" data-id="element-1377">Conflict</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1378">
            <td className="py-2 pr-4" data-id="element-1379">
              <code data-id="element-1380">429</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1381">
              <code data-id="element-1382">rate_limit_error</code>
            </td>
            <td className="py-2" data-id="element-1383">Too Many Requests</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1384">
            <td className="py-2 pr-4" data-id="element-1385">
              <code data-id="element-1386">500-503</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1387">
              <code data-id="element-1388">api_error</code>
            </td>
            <td className="py-2" data-id="element-1389">Server Error</td>
          </tr>
        </tbody>
      </table>

      <h2 id="common-codes" data-id="element-1390">Common Error Codes</h2>
      <p data-id="element-1391">Specific error codes help you handle different failure scenarios:</p>
      <ul data-id="element-1392">
        <li data-id="element-1393">
          <code data-id="element-1394">parameter_missing</code> - Required parameter not provided
        </li>
        <li data-id="element-1395">
          <code data-id="element-1396">invalid_parameter</code> - Parameter has invalid format or value
        </li>
        <li data-id="element-1397">
          <code data-id="element-1398">invalid_state</code> - Resource in wrong state for operation
        </li>
        <li data-id="element-1399">
          <code data-id="element-1400">duplicate_request</code> - Idempotency key already used with
          different parameters
        </li>
        <li data-id="element-1401">
          <code data-id="element-1402">rate_limited</code> - Request rate limit exceeded
        </li>
        <li data-id="element-1403">
          <code data-id="element-1404">screening_blocked</code> - Transaction blocked by compliance
          screening
        </li>
        <li data-id="element-1405">
          <code data-id="element-1406">insufficient_liquidity</code> - Not enough liquidity available
        </li>
        <li data-id="element-1407">
          <code data-id="element-1408">insufficient_balance</code> - Wallet balance too low
        </li>
        <li data-id="element-1409">
          <code data-id="element-1410">beneficiary_not_found</code> - Invalid beneficiary ID
        </li>
        <li data-id="element-1411">
          <code data-id="element-1412">invalid_currency</code> - Currency not supported
        </li>
      </ul>

      <h2 id="handling" data-id="element-1413">Handling Errors</h2>
      <p data-id="element-1414">Always check HTTP status codes and parse error responses:</p>
      <CodeBlock code={errorHandlingCode} data-id="element-1415" />

      <Callout type="tip" data-id="element-1416">
        Save the <code data-id="element-1417">request_id</code> from error responses. Include it when
        contacting support for faster troubleshooting.
      </Callout>

      <h2 id="troubleshooting" data-id="element-1418">Troubleshooting Checklist</h2>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-6" data-id="element-1419">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1420">
          Common Issues
        </h3>
        <div className="space-y-3" data-id="element-1421">
          <div className="flex items-start gap-3" data-id="element-1422">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1423" />
            <div data-id="element-1424">
              <strong className="text-slate-900" data-id="element-1425">401 Unauthorized:</strong>
              <p className="text-sm text-slate-600" data-id="element-1426">
                Check API key is correct and not expired. Verify Authorization
                header format.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-1427">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1428" />
            <div data-id="element-1429">
              <strong className="text-slate-900" data-id="element-1430">400 Bad Request:</strong>
              <p className="text-sm text-slate-600" data-id="element-1431">
                Review the <code data-id="element-1432">param</code> and <code data-id="element-1433">message</code> fields.
                Check parameter types and formats.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-1434">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1435" />
            <div data-id="element-1436">
              <strong className="text-slate-900" data-id="element-1437">429 Rate Limited:</strong>
              <p className="text-sm text-slate-600" data-id="element-1438">
                Implement exponential backoff. Check rate limit headers for
                retry timing.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-1439">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1440" />
            <div data-id="element-1441">
              <strong className="text-slate-900" data-id="element-1442">500 Server Error:</strong>
              <p className="text-sm text-slate-600" data-id="element-1443">
                Safe to retry with exponential backoff. If persistent, contact
                support with request_id.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-1444">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1445" />
            <div data-id="element-1446">
              <strong className="text-slate-900" data-id="element-1447">409 Conflict:</strong>
              <p className="text-sm text-slate-600" data-id="element-1448">
                Idempotency key reused with different parameters. Use a new key
                or verify request matches original.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Callout type="note" data-id="element-1449">
        For complete error conventions, see{' '}
        <a href="#/docs/api-conventions" className="text-blue-600 hover:text-blue-700" data-id="element-1450">
          API Conventions
        </a>
        .
      </Callout>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-1451">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1452">
          Related Guides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1453">
          <a href="#/docs/guides/idempotency" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1454">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1455">Idempotency</h4>
            <p className="text-sm text-slate-600" data-id="element-1456">
              Safely retry failed requests
            </p>
          </a>
          <a href="#/docs/api-conventions" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1457">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1458">
              API Conventions
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1459">
              Complete API specifications
            </p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}